import React, { useMemo } from 'react';
import { Grid3x3 } from 'lucide-react';

const RuleCombinationHeatmap = ({ data = [], rules = [], loading }) => {
  // Build a symmetric lookup: Map<tagA, Map<tagB, { avg_pnl, trade_count }>>
  const cellMap = useMemo(() => {
    const map = new Map();
    for (const row of data) {
      if (!map.has(row.rule_a_tag)) map.set(row.rule_a_tag, new Map());
      if (!map.has(row.rule_b_tag)) map.set(row.rule_b_tag, new Map());
      const cell = { avg_pnl: row.avg_pnl, trade_count: row.trade_count };
      map.get(row.rule_a_tag).set(row.rule_b_tag, cell);
      map.get(row.rule_b_tag).set(row.rule_a_tag, cell); // mirror
    }
    return map;
  }, [data]);

  // Compute min/max for color scaling
  const { minPnl, maxPnl } = useMemo(() => {
    if (data.length === 0) return { minPnl: -1, maxPnl: 1 };
    const values = data.map((d) => d.avg_pnl);
    return { minPnl: Math.min(...values), maxPnl: Math.max(...values) };
  }, [data]);

  const getCellColor = (value) => {
    if (value == null) return 'transparent';
    const absMax = Math.max(Math.abs(minPnl), Math.abs(maxPnl), 0.01);
    const normalized = value / absMax; // -1 to +1
    if (normalized >= 0) {
      const intensity = Math.min(normalized, 1);
      return `rgba(34,197,94,${(intensity * 0.65).toFixed(2)})`;
    } else {
      const intensity = Math.min(Math.abs(normalized), 1);
      return `rgba(239,68,68,${(intensity * 0.65).toFixed(2)})`;
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-4 animate-pulse">
        <div className="h-4 bg-muted rounded w-1/3 mb-4" />
        <div className="h-72 bg-muted rounded" />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <div className="flex items-center gap-2 mb-1">
        <Grid3x3 className="w-4 h-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-muted-foreground">Rule Combination Heatmap</h3>
      </div>
      <p className="text-xs text-muted-foreground/60 mb-3">
        Avg PnL when both rules were followed together (min 2 trades)
      </p>

      {rules.length === 0 || data.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <Grid3x3 className="w-8 h-8 text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">Not enough data yet.</p>
          <p className="text-xs text-muted-foreground/60 mt-1">
            Need at least 2 trades with both rules followed together.
          </p>
        </div>
      ) : (
        <div className="overflow-auto max-h-96">
          <table className="text-[10px] border-collapse min-w-max">
            <thead>
              <tr>
                {/* Corner cell */}
                <th className="w-24 min-w-[6rem]" />
                {rules.map((rule) => (
                  <th key={rule.tag} className="p-0">
                    <div
                      className="flex items-end justify-center text-muted-foreground font-normal pb-1"
                      style={{
                        writingMode: 'vertical-rl',
                        transform: 'rotate(180deg)',
                        height: '80px',
                        whiteSpace: 'nowrap',
                        maxWidth: '24px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      title={rule.label}
                    >
                      {rule.label}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rules.map((rowRule) => (
                <tr key={rowRule.tag}>
                  {/* Row label */}
                  <td
                    className="pr-2 text-right text-muted-foreground font-normal py-0.5 max-w-[6rem] truncate"
                    title={rowRule.label}
                  >
                    {rowRule.label}
                  </td>
                  {rules.map((colRule) => {
                    const isDiag = rowRule.tag === colRule.tag;
                    const cell = isDiag
                      ? null
                      : cellMap.get(rowRule.tag)?.get(colRule.tag) ?? null;

                    return (
                      <td
                        key={colRule.tag}
                        className="w-6 h-6 min-w-[1.5rem] border border-border/30 text-center align-middle"
                        style={{ backgroundColor: isDiag ? 'rgba(255,255,255,0.03)' : getCellColor(cell?.avg_pnl) }}
                        title={
                          cell
                            ? `${rowRule.label} + ${colRule.label}\nAvg PnL: $${cell.avg_pnl.toFixed(2)}\nTrades: ${cell.trade_count}`
                            : isDiag ? '—' : 'No data'
                        }
                      >
                        {isDiag ? (
                          <span className="text-muted-foreground/30">—</span>
                        ) : cell ? (
                          <span
                            className={`tabular-nums font-medium ${
                              cell.avg_pnl >= 0 ? 'text-green-300' : 'text-red-300'
                            }`}
                          >
                            {cell.avg_pnl >= 0 ? '+' : ''}
                            {cell.avg_pnl.toFixed(1)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground/20">·</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Legend */}
          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border/50">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'rgba(34,197,94,0.65)' }} />
              <span className="text-[10px] text-muted-foreground">Positive PnL</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'rgba(239,68,68,0.65)' }} />
              <span className="text-[10px] text-muted-foreground">Negative PnL</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm bg-muted/20" />
              <span className="text-[10px] text-muted-foreground">No data</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RuleCombinationHeatmap;
